import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '@/routes/paths';
import {
  Compass,
  Briefcase,
  LayoutGrid,
  RotateCw,
  ArrowLeftRight
} from 'lucide-react';
import { useSalesEmployeeData } from '../widgets/sales-employee/useSalesEmployeeData';
import { SalesEmployeeHero } from '../widgets/sales-employee/SalesEmployeeHero';
import { QuickActionsBar } from '../widgets/sales-employee/QuickActionsBar';
import { SalesEmployeeKpis } from '../widgets/sales-employee/SalesEmployeeKpis';
import { PriorityAlertStrip } from '../widgets/sales-employee/PriorityAlertStrip';
import { SalesActivityTrends } from '../widgets/sales-employee/SalesActivityTrends';
import { SalesTargetWidget } from '../widgets/sales-employee/SalesTargetWidget';
import { PersonalKpiRow } from '../widgets/sales-employee/PersonalKpiRow';
import { SalesFunnelWidget } from '../widgets/sales-employee/SalesFunnelWidget';
import { PriorityDealsCard } from '../widgets/sales-employee/PriorityDealsCard';
import { RecentLeadsCard } from '../widgets/sales-employee/RecentLeadsCard';
import { FollowupCenter } from '../widgets/sales-employee/FollowupCenter';
import { ActivityTimeline } from '../widgets/sales-employee/ActivityTimeline';
import { FieldActivityMap } from '../widgets/sales-employee/FieldActivityMap';
import { TodayStopsList } from '../widgets/sales-employee/TodayStopsList';
import { SamplingTrialsWidget } from '../widgets/sales-employee/SamplingTrialsWidget';
import { QuotationCommandWidget } from '../widgets/sales-employee/QuotationCommandWidget';
import { PendingDispatchWidget } from '../widgets/sales-employee/PendingDispatchWidget';
import { UpcomingVisitsWidget } from '../widgets/sales-employee/UpcomingVisitsWidget';
import { MyTasksWidget } from '../widgets/sales-employee/MyTasksWidget';
import { RecentCrmActivityFeed } from '../widgets/sales-employee/RecentCrmActivityFeed';
import { AiCopilotInsightCard } from '../widgets/sales-employee/AiCopilotInsightCard';
import { Client360Drawer } from '../widgets/sales-employee/Client360Drawer';
import { QuickActionModals } from '../widgets/sales-employee/QuickActionModals';
import { FloatingQuickDock } from '../widgets/sales-employee/FloatingQuickDock';
import { BdmFinancialsCard } from '../widgets/sales-employee/BdmFinancialsCard';
import { BdmDsrWidget } from '../widgets/sales-employee/BdmDsrWidget';
import { CurrencyExchangerModal } from '@/shared/components/currency';
import type { DealItem, LeadItem } from '../widgets/sales-employee/types';

/** `embedded`: shown as the Team Dashboard's "My work" tab, under that page's own banner. */
export function SalesEmployeeDashboard({ embedded = false }: { embedded?: boolean } = {}) {
  const navigate = useNavigate();
  const {
    scope,
    setScope,
    user,
    heroData,
    kpiCounts,
    alertData,
    targetData,
    priorityDeals,
    recentLeads,
    followups,
    markFollowupComplete,
    tasks,
    toggleTask,
    todayActivities,
    todayStops,
    samples,
    quotations,
    pendingDispatches,
    upcomingVisits,
    enquiryTrend,
    quotationTrend,
    crmActivities,
    dealsQuery,
    refetchAll,
  } = useSalesEmployeeData();

  // Workflow View Switcher: 'today' | 'pipeline' | 'all'
  const [activeView, setActiveView] = useState<'today' | 'pipeline' | 'all'>('today');
  const [isRefreshing, setIsRefreshing] = useState(false);

  // State for slide-over drawer and quick action modals
  const [selectedClient, setSelectedClient] = useState<any | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeModal, setActiveModal] = useState<'lead' | 'visit' | 'sample' | 'task' | 'followup' | null>(null);
  const [forexOpen, setForexOpen] = useState(false);

  // Can change scope check (management / admin tiers only)
  const canChangeScope = ['superadmin', 'admin', 'department_admin', 'manager', 'team_lead'].includes(
    user?.role ?? ''
  );

  const handleSelectDeal = (deal: DealItem) => {
    setSelectedClient({
      id: deal.id,
      type: 'deal',
      name: deal.client,
      company: deal.name,
      phone: deal.phone,
      email: deal.email,
      city: deal.city,
      stage: deal.stageLabel,
      value: deal.value,
      notes: `Next follow-up: ${deal.nextFollowup || 'Not scheduled'}. Win probability: ${deal.probability}%.`,
    });
    setDrawerOpen(true);
  };

  const handleSelectLead = (lead: LeadItem) => {
    setSelectedClient({
      id: lead.id,
      type: 'lead',
      name: lead.name,
      company: lead.company,
      phone: lead.phone,
      email: lead.email,
      stage: lead.statusLabel,
      notes: lead.notes,
    });
    setDrawerOpen(true);
  };

  const handleCompleteFollowup = (id: string) => {
    markFollowupComplete(id);
  };

  const handleOpenFollowup = (item: import('../widgets/sales-employee/types').FollowupItem) => {
    const routeByType: Record<string, string> = {
      quotation: `/quotations/${item.parentId}`,
      proforma: `/proforma/${item.parentId}`,
      invoice: `/invoices/${item.parentId}`,
      lead: `/pipeline/${item.parentId}`,
      // Deals were merged into the unified Pipeline. Keeping the old /deals
      // path here made dashboard follow-ups land on a legacy record surface
      // (or an empty/error page) instead of the client's active Pipeline
      // record and its shared FollowupPanel.
      deal: `/pipeline/${item.parentId}`,
    };
    // A sample's follow-up opens that sample (it has no #followups page).
    // An invoice follow-up holds the accounts invoice id — open that invoice.
    if (item.relatedType === 'invoice') { navigate(`/invoices?invoice=${encodeURIComponent(item.parentId)}`); return; }
    if (item.relatedType === 'sample') { navigate(`/sampling?sample=${encodeURIComponent(item.parentId)}`); return; }
    // Every document/record detail page has the shared FollowupPanel with this
    // fragment, so a dashboard row lands on the exact client's follow-up log.
    navigate(`${routeByType[item.relatedType] || `/pipeline/${item.parentId}`}#followups`);
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await refetchAll();
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleDrawerAction = (action: 'call' | 'whatsapp' | 'email' | 'quote' | 'followup' | 'sample') => {
    if (action === 'quote') {
      if (selectedClient?.id && selectedClient?.type === 'deal') {
        navigate(`${ROUTES.quotationNew}?dealId=${encodeURIComponent(selectedClient.id)}`);
      } else {
        navigate(ROUTES.quotationNew);
      }
    } else if (action === 'sample') {
      setActiveModal('sample');
    } else if (action === 'followup') {
      setActiveModal('followup');
    }
  };

  return (
    <div className="mx-auto max-w-[1720px] pb-16 font-sans">
      {/* 1. Hero Command Banner */}
      <SalesEmployeeHero
        displayName={user?.displayName || 'there'}
        pipelineValue={heroData.pipelineValue}
        openDealsCount={heroData.openDealsCount}
        wonMonthValue={heroData.wonMonthValue}
        wonDealsCount={heroData.wonDealsCount}
        collectedMonth={heroData.collectedMonth}
        pipelineForeign={heroData.pipelineForeign}
        monthForeign={heroData.monthForeign}
        totalLeads={heroData.totalLeads}
        leadsWeek={heroData.leadsWeek}
        overdueCount={heroData.overdueCount}
        visitsCount={heroData.visitsCount}
        variant={embedded ? 'section' : 'hero'}
      />

      {/* 2. Permanent Quick Actions Bar */}
      <QuickActionsBar
        onOpenModal={(type) => setActiveModal(type)}
        onOpenForex={() => setForexOpen(true)}
      />

      {/* 3. Sectional Workflow Tabs + Live Status Bar */}
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3 rounded-card border border-border bg-card p-2 shadow-xs">
        {/* Segmented Control */}
        <div role="group" aria-label="Dashboard sections" className="flex flex-wrap items-center gap-1">
          <button
            type="button"
            onClick={() => setActiveView('today')}
            aria-pressed={activeView === 'today'}
            className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-body-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
              activeView === 'today'
                ? 'bg-primary text-on-primary'
                : 'text-on-surface-variant hover:bg-surface-container hover:text-on-surface'
            }`}
          >
            <Compass className="h-4 w-4" aria-hidden />
            <span>Today</span>
          </button>

          <button
            type="button"
            onClick={() => setActiveView('pipeline')}
            aria-pressed={activeView === 'pipeline'}
            className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-body-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
              activeView === 'pipeline'
                ? 'bg-primary text-on-primary'
                : 'text-on-surface-variant hover:bg-surface-container hover:text-on-surface'
            }`}
          >
            <Briefcase className="h-4 w-4" aria-hidden />
            <span>Pipeline</span>
          </button>

          <button
            type="button"
            onClick={() => setActiveView('all')}
            aria-pressed={activeView === 'all'}
            className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-body-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
              activeView === 'all'
                ? 'bg-primary text-on-primary'
                : 'text-on-surface-variant hover:bg-surface-container hover:text-on-surface'
            }`}
          >
            <LayoutGrid className="h-4 w-4" aria-hidden />
            <span>All sections</span>
          </button>
        </div>

        {/* Live Status Indicators & Refresh */}
        <div className="flex items-center gap-2 pr-1">
          {/* Live Forex Desk button */}
          <button
            type="button"
            onClick={() => setForexOpen(true)}
            aria-label="Open the currency desk"
            className="flex cursor-pointer items-center gap-1.5 rounded-lg border border-primary/30 bg-primary-container px-3 py-1.5 text-body-sm font-medium text-primary-dim transition-colors hover:bg-primary-container focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <ArrowLeftRight className="h-3.5 w-3.5 text-primary" aria-hidden />
            <span>Currency desk</span>
          </button>

          <span
            role="status"
            aria-live="polite"
            className="flex items-center gap-2 rounded-lg bg-surface-variant px-3 py-1.5 text-data-xs text-on-surface-variant"
          >
            <span className={`h-2 w-2 rounded-full ${isRefreshing ? 'bg-warning' : 'bg-success'}`} aria-hidden />
            {isRefreshing ? 'Refreshing' : 'Live'}
          </span>

          <button
            type="button"
            onClick={handleRefresh}
            aria-label="Refresh this dashboard"
            className="flex h-8 w-8 items-center justify-center rounded-lg border border-border text-on-surface-variant transition-colors hover:bg-surface-container hover:text-on-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <RotateCw className={`h-3.5 w-3.5 ${isRefreshing ? 'animate-spin text-primary' : ''}`} aria-hidden />
          </button>
        </div>
      </div>

      {/* ─── VIEW 1: TODAY'S FOCUS ────────────────────────────────────────── */}
      {(activeView === 'today' || activeView === 'all') && (
        <div className="space-y-5 animate-in fade-in duration-300 motion-reduce:animate-none">
          {/* 3. Today At A Glance (8-KPI Grid + Scope Switcher + Date) */}
          <SalesEmployeeKpis
            scope={scope}
            onScopeChange={setScope}
            canChangeScope={canChangeScope}
            kpiCounts={kpiCounts}
          />

          {/* 4. Priority Alert Strip */}
          <PriorityAlertStrip
            overdueCount={alertData.overdueCount}
            dueTodayCount={alertData.dueTodayCount}
            hotLeadsCount={alertData.hotLeadsCount}
            expiringQuotesCount={alertData.expiringQuotesCount}
            sampleFeedbackCount={alertData.sampleFeedbackCount}
            overdueDispatchesCount={alertData.overdueDispatchesCount}
          />

          {/* 5. BDM Financials & Credit Recovery (Point 6) */}
          <BdmFinancialsCard />

          {/* 6. BDM Daily Sales Report (DSR) Scorecard (Point 7) */}
          <BdmDsrWidget />

          {/* Field Activity GPS (7) + Today's Stops (5) */}
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <FieldActivityMap stops={todayStops} />
            </div>
            <div className="lg:col-span-5">
              <TodayStopsList stops={todayStops} />
            </div>
          </div>

          {/* Follow-ups Command Center (7) + Today's Activity Timeline (5) */}
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <FollowupCenter
                followups={followups}
                onComplete={handleCompleteFollowup}
                onNewFollowup={() => setActiveModal('followup')}
                onOpenFollowup={handleOpenFollowup}
                repName={user?.displayName || ''}
              />
            </div>
            <div className="lg:col-span-5">
              <ActivityTimeline activities={todayActivities} />
            </div>
          </div>

          {/* Pending Dispatch (6) + Upcoming Visits (6) */}
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-12">
            <div className="lg:col-span-6">
              <PendingDispatchWidget dispatches={pendingDispatches} />
            </div>
            <div className="lg:col-span-6">
              <UpcomingVisitsWidget visits={upcomingVisits} />
            </div>
          </div>

          {/* My Tasks (6) + Recent CRM Activity Feed (6) */}
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-12">
            <div className="lg:col-span-6">
              <MyTasksWidget
                tasks={tasks}
                onToggleTask={toggleTask}
                onNewTask={() => setActiveModal('task')}
              />
            </div>
            <div className="lg:col-span-6">
              <RecentCrmActivityFeed activities={crmActivities} />
            </div>
          </div>
        </div>
      )}

      {/* ─── VIEW 2: COMMERCIAL PIPELINE ──────────────────────────────────── */}
      {(activeView === 'pipeline' || activeView === 'all') && (
        <div className={`space-y-5 animate-in fade-in duration-300 motion-reduce:animate-none ${activeView === 'all' ? 'mt-5' : ''}`}>
          {/* Sales Activity Trends (Left) & Sales Target Progress (Right with Dual Mode) */}
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-12">
            <div className="lg:col-span-8">
              <SalesActivityTrends
                enquiryPoints={enquiryTrend}
                quotationPoints={quotationTrend}
              />
            </div>
            <div className="lg:col-span-4">
              <SalesTargetWidget
                target={targetData.target}
                achieved={targetData.achieved}
                expectedClosing={targetData.expectedClosing}
                hasTarget={targetData.hasTarget}
              />
            </div>
          </div>

          {/* Personal KPI Row (4 Cards) */}
          <PersonalKpiRow
            myLeadsCount={heroData.totalLeads}
            myDealsCount={heroData.openDealsCount}
            leadsTodayCount={alertData.hotLeadsCount}
            tasksOpenCount={kpiCounts.openTasks}
          />

          {/* Funnel (4) + Priority Deals (4) + Recent Leads (4) */}
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-12">
            <div className="lg:col-span-4">
              <SalesFunnelWidget deals={dealsQuery.data?.deals ?? []} />
            </div>
            <div className="lg:col-span-4">
              <PriorityDealsCard deals={priorityDeals} onSelectDeal={handleSelectDeal} />
            </div>
            <div className="lg:col-span-4">
              <RecentLeadsCard leads={recentLeads} onSelectLead={handleSelectLead} />
            </div>
          </div>

          {/* Samples & Machine Trials (6) + Quotation Command (6) */}
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-12">
            <div className="lg:col-span-6">
              <SamplingTrialsWidget
                samples={samples}
                onNewSample={() => setActiveModal('sample')}
              />
            </div>
            <div className="lg:col-span-6">
              <QuotationCommandWidget quotations={quotations} />
            </div>
          </div>
        </div>
      )}

      {/* 13. ZYNX AI Copilot Insight Card */}
      <div className="mt-5">
        <AiCopilotInsightCard
          alertData={alertData}
          heroData={heroData}
        />
      </div>

      {/* Slide-over Client / Lead 360 Drawer */}
      <Client360Drawer
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        data={selectedClient}
        currentUser={user}
        onAction={handleDrawerAction}
      />

      {/* Quick Action Modals */}
      <QuickActionModals
        modalType={activeModal}
        onClose={() => setActiveModal(null)}
      />

      {/* Floating Bottom Quick Dock (on scroll) */}
      <FloatingQuickDock onOpenModal={(type) => setActiveModal(type)} />

      {/* Currency Exchanger & Live Forex Desk Modal */}
      <CurrencyExchangerModal
        open={forexOpen}
        onClose={() => setForexOpen(false)}
        defaultBase="USD"
        defaultQuote="INR"
      />
    </div>
  );
}
